import React from "react";

import { useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import queryString from "query-string";
import { Navigate, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";

import WeezPayAPIProvider from "api/components/WeezPayAPIProvider";
import useWidgetQuery from "api/hooks/useWidgetQuery";
import Card from "components/Card";
import ErrorCard from "components/ErrorCard";
import Loader from "components/Loader";
import PageGlobalLoader from "components/PageGlobalLoader";
import RouteFilter from "components/RouteFilter";
import WidgetContainer from "components/WidgetContainer";
import {
  PAYMENT_CONTEXT_TOP_UP,
  PAYMENT_CONTEXT_FAST_TOP_UP,
  PAYMENT_CONTEXT_PREORDER, PAYMENT_CONTEXT_TOP_UP_PREORDER,
} from "constants/payment";
import CartContextProvider from "contexts/CartContextProvider";
import OrderContextProvider from "contexts/OrderContextProvider";
import useOrganizationId from "hooks/useOrganizationId";
import useRoutePaths from "hooks/useRoutePaths";
import useAuthentication from "libs/authentication/hooks/useAuthentication";
import { I18nProvider } from "libs/i18n";
import { getLocaleFiles } from "locales/config";
import {
  ACTIVITY,
  BLOCK_TAG,
  CANCEL_REFUND_CONFIRM,
  CART,
  CART_CUSTOMER_FORM,
  CART_PAYMENT,
  CART_WALLET_SELECTION,
  DONATION,
  FAST_TOP_UP,
  FAST_TOP_UP_CONFIRMATION,
  HOME,
  LOGIN,
  LOGIN_AKAMAI_CALLBACK,
  LOGIN_CAS_CALLBACK,
  LOGIN_REACH_FIVE_CALLBACK,
  LOGOUT_REACH_FIVE_CALLBACK,
  MOBILE_PAYMENT,
  ORDER,
  ORDERS,
  ORDER_CONFIRMATION,
  ORDER_SUMMARY,
  PASSWORD_RESET,
  PASSWORD_RESET_CONFIRM,
  PAYMENT,
  PERSONAL_INFO,
  REFUND,
  SIGN_UP,
  SHOP,
  SHOP_PRODUCT,
  SHOPS,
  TOP_UP,
  TOP_UP_CONFIRMATION,
  WALLET_CREATE,
  WALLET_TICKET,
  WALLET_UPDATE,
} from "routers/routes";
import ThemeProvider from "styles/ThemeProvider";
import themeDefaultConstants from "styles/themeDefaultConstants";

/* The subcomponents */
import SignUpContextProvider from "../../contexts/SignUpContextProvider";
import WalletContextProvider from "../../contexts/WalletContextProvider";
import DateLocalizationProvider from "./components/DateLocalizationProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import RouteFilterAuthentication from "./components/RouteFilterAuthentication";
import RouteFilterWidgetConfig from "./components/RouteFilterWidgetConfig";

const ActivityPage = React.lazy(() => import("pages/ActivityPage"));
const BlockTagPage = React.lazy(() => import("pages/BlockTagPage"));
const CancelRefundConfirm = React.lazy(() => import("pages/CancelRefundConfirmPage"));
const CartCustomerFormPage = React.lazy(() => import("pages/CartCustomerFormPage"));
const CartFormPage = React.lazy(() => import("pages/CartFormPage"));
const CartInformationPage = React.lazy(() => import("pages/CartInformationPage"));
const CartPage = React.lazy(() => import("pages/CartPage"));
const CartPaymentPage = React.lazy(() => import("pages/CartPaymentPage"));
const CartWalletSelectionPage = React.lazy(() => import("pages/CartWalletSelectionPage"));
const DonationPage = React.lazy(() => import("pages/DonationPage"));
const HomePage = React.lazy(() => import("pages/HomePage"));
const FastTopUpConfirmationPage = React.lazy(() => import("pages/FastTopUpConfirmationPage"));
const FastTopUpPage = React.lazy(() => import("pages/FastTopUpPage"));
const ErrorPage = React.lazy(() => import("pages/ErrorPage"));
const LoginCasCallbackPage = React.lazy(() => import("pages/LoginCasCallbackPage"));
const LoginPage = React.lazy(() => import("pages/LoginPage"));
const LoginAkamaiCallbackPage = React.lazy(() => import("pages/LoginAkamaiCallbackPage"));
const LoginReachFiveCallbackPage = React.lazy(() => import("pages/LoginReachFiveCallbackPage"));
const LogoutReachFiveCallbackPage = React.lazy(() => import("pages/LogoutReachFiveCallbackPage"));
const MobilePaymentPage = React.lazy(() => import("pages/MobilePaymentPage"));
const OrderConfirmationPage = React.lazy(() => import("pages/OrderConfirmationPage"));
const OrderInvoiceFormPage = React.lazy(() => import("pages/OrderInvoiceFormPage"));
const OrderPage = React.lazy(() => import("pages/OrderPage"));
const OrdersPage = React.lazy(() => import("pages/OrdersPage"));
const OrderSummaryPage = React.lazy(() => import("pages/OrderSummaryPage/OrderSummaryPage"));
const PasswordResetPage = React.lazy(() => import("pages/PasswordResetPage"));
const PasswordResetConfirm = React.lazy(() => import("pages/PasswordResetConfirmPage"));
const PaymentPage = React.lazy(() => import("pages/PaymentPage"));
const PersonalInformationPage = React.lazy(() => import("pages/PersonalInformationPage"));
const RefundPage = React.lazy(() => import("pages/RefundPage"));
const ShopMenuCompositionPage = React.lazy(() => import("pages/ShopMenuCompositionPage"));
const ShopPage = React.lazy(() => import("pages/ShopPage"));
const ShopProductPage = React.lazy(() => import("pages/ShopProductPage"));
const ShopsPage = React.lazy(() => import("pages/ShopsPage"));
const SignupPage = React.lazy(() => import("pages/SignupPage"));
const TopUpPage = React.lazy(() => import("pages/TopUpPage"));
const TopUpConfirmationPage = React.lazy(() => import("pages/TopUpConfirmationPage"));
const WalletPage = React.lazy(() => import("pages/WalletPage"));
const WalletTicketPage = React.lazy(() => import("pages/WalletTicketPage"));
const WalletUpdatePage = React.lazy(() => import("pages/WalletUpdatePage"));

/**
 * Widget router page.
 * It's the router part that's dedicated to the widget part (the whole cashless application actually)
 * This is separated of the App router page for now due to the cohabitation with the old gadget.
 */
const Widget = ({
  locales = null,
  theme = null,
  widgetUUID = null,
}) => {
  const authentication = useAuthentication();
  const organizationId = useOrganizationId();
  const routePaths = useRoutePaths();
  const [localeFiles, setLocaleFiles] = React.useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPaymentPageCallback = searchParams.get("isPaymentPageCallback");
  const redirectToWidgetPage = searchParams.get("redirectToWidgetPage");
  const context = searchParams.get("context");
  const cartUuid = searchParams.get("cartUUID");
  const fastTopUpId = searchParams.get("fastTopUpId");
  const topUpId = searchParams.get("topUpId");
  const isRedirectedFromNewPaymentPage = searchParams.get("from_payment_page");
  const isFromRefundFlow = searchParams.get("isFromRefundFlow") === "true";

  const isDarkModeEnabled = useMediaQuery("(prefers-color-scheme: dark)");

  const widgetQuery = useWidgetQuery(
    {
      refetchOnWindowFocus: false,
    },
  );

  React.useEffect(
    () => {
      if (isPaymentPageCallback) {
        if (context === PAYMENT_CONTEXT_TOP_UP && topUpId) {
          navigate(
            routePaths[TOP_UP_CONFIRMATION]({
              context,
              topUpId,
              autoRedirect: isRedirectedFromNewPaymentPage,
            }),
            { replace: true },
          );
        }

        if (context === PAYMENT_CONTEXT_TOP_UP_PREORDER && topUpId) {
          navigate(routePaths[CART]({ isRedirectionFromPaymentPage: true }), { replace: true });
        }

        if (context === PAYMENT_CONTEXT_FAST_TOP_UP && fastTopUpId) {
          navigate(
            routePaths[FAST_TOP_UP_CONFIRMATION]({
              context,
              fastTopUpId,
              autoRedirect: isRedirectedFromNewPaymentPage,
            }),
            { replace: true },
          );
        }

        if (context === PAYMENT_CONTEXT_PREORDER && cartUuid) {
          navigate(
            routePaths[ORDER_CONFIRMATION]({
              context,
              cartUuid,
              autoRedirect: isRedirectedFromNewPaymentPage,
            }),
            { replace: true },
          );
        }
      } else if (redirectToWidgetPage) {
        window.location.replace(redirectToWidgetPage);
      }
    },
    [
      cartUuid,
      context,
      fastTopUpId,
      isPaymentPageCallback,
      navigate,
      redirectToWidgetPage,
      isRedirectedFromNewPaymentPage,
      routePaths,
      topUpId,
      isFromRefundFlow,
    ],
  );

  React.useEffect(
    () => {
      const loadLocales = async () => {
        if (!localeFiles) {
          setLocaleFiles(await getLocaleFiles());
        }
      };
      loadLocales()
        .then()
        .catch();
    },
    [localeFiles],
  );

  // Define the languages of the widget
  const localFromUrlQueryParam = React.useMemo(
    () => {
      const queryParam = queryString.parse(window.location.search);
      const hashParam = queryString.parse(window.location.hash);
      return queryParam?.lang || hashParam?.lang || (hashParam && hashParam["?lang"]);
    },
    [],
  );

  // fetch origin parameter if any
  const origin = React.useMemo(
    () => {
      const { query } = queryString.parseUrl(window.location.href);
      const hash = queryString.parse(window.location.hash);
      return query?.o || hash?.o;
    },
    [],
  );

  if (widgetQuery?.isLoading || !widgetQuery?.data || !localeFiles) {
    return (
      <PageGlobalLoader margin="20px" />
    );
  }

  if (widgetQuery?.isError) {
    const onClickRetry = () => {
      if (widgetQuery?.isError && !widgetQuery.isFetching) {
        widgetQuery.refetch()
          .then()
          .catch();
      }
    };

    return (
      <I18nProvider
        backend="fluent"
        defaultLocale="en"
        locale={localFromUrlQueryParam || "en"}
        allowedLocales={localFromUrlQueryParam ? [localFromUrlQueryParam] : ["en"]}
        isLocaleLocked
        files={localeFiles}
        localStorageKey={!localFromUrlQueryParam ? `locale-${widgetUUID}` : null}
      >
        <Card margin="8px">
          {widgetQuery.isFetching
            ? (<Loader />)
            : (<ErrorCard onClick={onClickRetry} />)}
        </Card>
      </I18nProvider>
    );
  }

  return (
    <ThemeProvider
      constants={{ ...themeDefaultConstants, ...widgetQuery?.data?.theme, ...theme }}
      mode={isDarkModeEnabled ? "dark" : "light"}
    >
      <WidgetContainer padding="8px">
        <I18nProvider
          backend="fluent"
          defaultLocale={widgetQuery?.data?.default_locale}
          locale={localFromUrlQueryParam || widgetQuery?.data?.default_locale}
          allowedLocales={widgetQuery?.data?.allowed_locales}
          isLocaleLocked={!!localFromUrlQueryParam}
          files={localeFiles}
          overwrittenLocales={locales === null ? widgetQuery?.data?.overriden_translations : locales}
          localStorageKey={!localFromUrlQueryParam ? `locale-${widgetUUID}` : null}
        >
          <WeezPayAPIProvider>
            <DateLocalizationProvider>
              <WalletContextProvider organizationId={organizationId} widgetUUID={widgetUUID}>
                <OrderContextProvider organizationId={organizationId} widgetUUID={widgetUUID}>
                  <ErrorBoundary>
                    <Routes>
                      <Route
                        path="activity"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ACTIVITY}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <ActivityPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="block-tag"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={BLOCK_TAG}
                              isCashlessAllowed
                            >
                              <RouteFilter
                                isAllowed={widgetQuery?.data?.isSelfTagBlockingEnabled}
                                onNotAllowed={() => <Navigate to={routePaths[HOME]()} replace />}
                              >
                                <React.Suspense fallback={<PageGlobalLoader />}>
                                  <BlockTagPage />
                                </React.Suspense>
                              </RouteFilter>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <CartPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart/form"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <CartFormPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart/customer-form"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART_CUSTOMER_FORM}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <CartCustomerFormPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart/information"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <CartInformationPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart/payment"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART_PAYMENT}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <CartPaymentPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="cart/wallet-selection"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CART_WALLET_SELECTION}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <CartWalletSelectionPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="donation"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={DONATION}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <DonationPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="fast-topup"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={FAST_TOP_UP}
                              isFastTopUpAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <FastTopUpPage origin={origin} />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="fast-top-up-confirmation"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={FAST_TOP_UP_CONFIRMATION}
                              isFastTopUpAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <FastTopUpConfirmationPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="login"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={LOGIN}
                              isCashlessAllowed
                            >
                              <SignUpContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                  <LoginPage />
                                </React.Suspense>
                              </SignUpContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="login/akamai"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={LOGIN_AKAMAI_CALLBACK}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <LoginAkamaiCallbackPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="login/cas"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={LOGIN_CAS_CALLBACK}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <LoginCasCallbackPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="login/reachfive"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={LOGIN_REACH_FIVE_CALLBACK}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <LoginReachFiveCallbackPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="logout/reachfive"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={LOGOUT_REACH_FIVE_CALLBACK}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <LogoutReachFiveCallbackPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="me"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={PERSONAL_INFO}
                              isCashlessAllowed
                            >
                              <RouteFilter
                                isAllowed={!authentication?.authInfo?.config?.should_hide_personal_information_block}
                                onNotAllowed={() => <Navigate to={routePaths[HOME]()} replace />}
                              >
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <PersonalInformationPage />
                                </React.Suspense>
                              </RouteFilter>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="order-confirmation"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ORDER_CONFIRMATION}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <OrderConfirmationPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="order-summary"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ORDER_SUMMARY}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <OrderSummaryPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="orders"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ORDERS}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <OrdersPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="orders/:orderId/invoice-form"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ORDER}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <OrderInvoiceFormPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="orders/:orderId"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={ORDER}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <OrderPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="signup"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={SIGN_UP}
                              isCashlessAllowed
                              isPreorderAllowed
                            >
                              <SignUpContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <SignupPage />
                                </React.Suspense>
                              </SignUpContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="shops"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={SHOPS}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <ShopsPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="shops/:shopId"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={SHOP}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <ShopPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="/shops/:shopId/products/:productId"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={SHOP_PRODUCT}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader />}>
                                  <ShopProductPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="shops/:shopId/menus/:menuId/rows/:rowId"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={SHOP}
                              isCashlessAllowed
                              isPreorderAllowed
                              isPreorderCartFlow
                            >
                              <CartContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                  <ShopMenuCompositionPage />
                                </React.Suspense>
                              </CartContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="password_reset"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={PASSWORD_RESET}
                              isCashlessAllowed
                            >
                              <SignUpContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                  <PasswordResetPage />
                                </React.Suspense>
                              </SignUpContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="payment"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={PAYMENT}
                              isCashlessAllowed
                              isPreorderAllowed
                              isFastTopUpAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <PaymentPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="refund"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={REFUND}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <RefundPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="refund/cancellation"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={CANCEL_REFUND_CONFIRM}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <CancelRefundConfirm />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="topup"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={TOP_UP}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <TopUpPage origin={origin} />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="top-up-confirmation"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={TOP_UP_CONFIRMATION}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <TopUpConfirmationPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="users/reset_password"
                        element={(
                          <RouteFilterAuthentication notAuthenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={PASSWORD_RESET_CONFIRM}
                              isCashlessAllowed
                            >
                              <SignUpContextProvider>
                                <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                  <PasswordResetConfirm />
                                </React.Suspense>
                              </SignUpContextProvider>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="wallet/create"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={WALLET_CREATE}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <WalletPage origin={origin} />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="wallet/update"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={WALLET_UPDATE}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <WalletUpdatePage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="wallet/:walletId/mobilepayment"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={MOBILE_PAYMENT}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <MobilePaymentPage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="wallets/:walletId/tickets/:ticketBarcode"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={WALLET_TICKET}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader displayCard />}>
                                <WalletTicketPage
                                  displayQr={widgetQuery.data?.display_QR_in_wallet_enabled !== false}
                                />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="/"
                        element={(
                          <RouteFilterAuthentication authenticatedAuthorized>
                            <RouteFilterWidgetConfig
                              widget={widgetQuery?.data}
                              route={HOME}
                              isCashlessAllowed
                            >
                              <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                                <HomePage />
                              </React.Suspense>
                            </RouteFilterWidgetConfig>
                          </RouteFilterAuthentication>
                      )}
                      />
                      <Route
                        path="*"
                        element={(
                          <React.Suspense fallback={<PageGlobalLoader margin="12px" />}>
                            <ErrorPage />
                          </React.Suspense>
                      )}
                      />
                    </Routes>
                  </ErrorBoundary>
                </OrderContextProvider>
              </WalletContextProvider>
            </DateLocalizationProvider>
          </WeezPayAPIProvider>
        </I18nProvider>
      </WidgetContainer>
    </ThemeProvider>
  );
};

Widget.propTypes = {
  /**
   * The translations to override in the widget.
   */
  locales: PropTypes.object,
  /**
   * The theme to inject and use in the widget.
   */
  theme: PropTypes.object,
  /**
   * The widget UUID.
   */
  widgetUUID: PropTypes.string,
};

export default Widget;
